import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { getReservationModeLabels } from '@iheartjane/menu';
import type {
  Nullable,
  ReservationMode,
  ShopperReservationMode,
} from '@iheartjane/types';
import { useMemo } from 'react';

import { useAvailableShopperReservationModes } from '@jane/shared-ecomm/pages';
import {
  getOrderingSchedule,
  isAcceptingOrders,
} from '@jane/shared-ecomm/util';
import type { StoreSchedule } from '@jane/shared-ecomm/util';
import { useReservationSlots } from '@jane/shared/data-access';
// eslint-disable-next-line no-restricted-imports
import type { Store } from '@jane/shared/models';
import type { Address, Store as zStore } from '@jane/shared/types';

interface UseReservationAvailability {
  address: Nullable<Address>;
  reservationMode?: ReservationMode;
  store: Store | zStore;
}
interface ReservationAvailability {
  canGoToCheckout: boolean;
  checkoutButtonLabel: string;
  curbsideLabel: string;
  curbsideSchedule: StoreSchedule | null;
  deliveryLabel: string;
  deliverySchedule: StoreSchedule | null;
  hasCurbsideSlots: boolean;
  hasDeliverySlots: boolean;
  hasPickupSlots: boolean;
  isAcceptingDeliveryOrders: boolean;
  isLoading: boolean;
  modesToRender: ShopperReservationMode[];
  pickupLabel: string;
  pickupSchedule: StoreSchedule | null;
  refetchCurbside: () => void;
  refetchDelivery: () => void;
  refetchPickup: () => void;
}

export const useReservationAvailability = ({
  address,
  reservationMode,
  store,
}: UseReservationAvailability): ReservationAvailability => {
  const curbsideFlagOn = useFlag(FLAGS.c1CurbsideFulfillment) ?? false;
  const hasAddress = !!address?.lat && !!address?.lng && !!address?.zipcode;

  /**
   * If there is no address, we can't get delivery slots
   * this check keeps the tab from being disabled
   */
  const isAcceptingDeliveryOrders = useMemo(
    () => isAcceptingOrders(store as zStore, 'delivery'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      store.time_zone_identifier,
      store.delivery_hours,
      store.allow_off_hours_ordering,
      store.allow_future_day_ordering,
    ]
  );

  const isAcceptingCurbsideOrders = useMemo(
    () => isAcceptingOrders(store as zStore, 'curbside'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      store.time_zone_identifier,
      store.curbside_hours,
      store.allow_off_hours_ordering,
      store.allow_future_day_ordering,
    ]
  );

  const { deliverySchedule, pickupSchedule, curbsideSchedule } = useMemo(
    () =>
      getOrderingSchedule({
        store,
        type: ['delivery', 'pickup', 'curbside'],
      }),
    [store]
  );

  const isPickupOpen = pickupSchedule
    ? !pickupSchedule?.isAlwaysClosed()
    : false;
  const isDeliveryOpen = deliverySchedule
    ? !deliverySchedule?.isAlwaysClosed()
    : false;
  const isCurbsideOpen = curbsideSchedule
    ? !curbsideSchedule?.isAlwaysClosed()
    : false;

  const scheduleOpenModes: ShopperReservationMode[] = [];
  if (isDeliveryOpen) scheduleOpenModes.push('delivery');
  if (isPickupOpen) scheduleOpenModes.push('pickup');
  if (isCurbsideOpen) scheduleOpenModes.push('curbside');

  // Now-aware curbside signal so the mandatory pickup fallback matches checkout.
  const isCurbsideAcceptingNow = isCurbsideOpen && isAcceptingCurbsideOrders;

  const curbsideAwareModes = useAvailableShopperReservationModes(
    store,
    scheduleOpenModes,
    isCurbsideAcceptingNow
  );
  const curbsideAwareModesKey = curbsideAwareModes.join(',');
  const modesToRender = useMemo(
    () =>
      curbsideFlagOn
        ? curbsideAwareModes
        : scheduleOpenModes.filter((mode) => mode !== 'curbside'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      curbsideFlagOn,
      curbsideAwareModesKey,
      isDeliveryOpen,
      isPickupOpen,
      isCurbsideOpen,
    ]
  );

  const {
    data: pickupSlots,
    isFetching: pickupFetching,
    isLoading: pickupInitialLoading,
    refetch: refetchPickup,
  } = useReservationSlots(
    {
      reservationMode: 'pickup',
      storeId: store?.id,
    },
    { enabled: isPickupOpen }
  );

  const {
    data: deliverySlots,
    isFetching: deliveryFetching,
    isLoading: deliveryInitialLoading,
    refetch: refetchDelivery,
  } = useReservationSlots(
    {
      reservationMode: 'delivery',
      storeId: store?.id,
      lat: address?.lat,
      lng: address?.lng,
      zipcode: address?.zipcode,
    },
    {
      enabled: isDeliveryOpen,
    }
  );

  const {
    data: curbsideSlots,
    isFetching: curbsideFetching,
    isLoading: curbsideInitialLoading,
    refetch: refetchCurbside,
  } = useReservationSlots(
    {
      reservationMode: 'curbside',
      storeId: store?.id,
    },
    { enabled: isCurbsideOpen && curbsideFlagOn }
  );

  return useMemo(() => {
    const { pickupLabel, deliveryLabel, curbsideLabel } =
      getReservationModeLabels((store as Store).reservation_mode_labels);

    const hasPickupSlots = pickupFetching
      ? true
      : (pickupSlots?.reservation_slots?.length ?? 0) > 0;
    const hasDeliverySlots = deliveryFetching
      ? true
      : (deliverySlots?.reservation_slots?.length ?? 0) > 0;
    const hasCurbsideSlots = curbsideFetching
      ? true
      : (curbsideSlots?.reservation_slots?.length ?? 0) > 0;

    const hasSelectedDelivery = reservationMode === 'delivery';
    // Flag-gated so flag-off collapses to the pickup branch (master parity).
    const hasSelectedCurbside =
      curbsideFlagOn && reservationMode === 'curbside';
    const missingAddress = !!hasSelectedDelivery && !hasAddress;

    return {
      canGoToCheckout: hasSelectedDelivery
        ? hasAddress && hasDeliverySlots
        : hasSelectedCurbside
          ? hasCurbsideSlots
          : hasPickupSlots,
      checkoutButtonLabel: missingAddress
        ? 'Please enter your address'
        : 'Proceed to checkout',
      curbsideLabel,
      curbsideSchedule,
      deliveryLabel,
      deliverySchedule,
      hasCurbsideSlots,
      hasPickupSlots,
      hasDeliverySlots: !hasAddress ? true : hasDeliverySlots,
      isAcceptingDeliveryOrders,
      // Flag-gated so flag-off matches master (isFetching); flag on uses
      // isLoading to avoid a skeleton flash on background refetch.
      isLoading: curbsideFlagOn
        ? hasSelectedDelivery
          ? deliveryInitialLoading
          : hasSelectedCurbside
            ? curbsideInitialLoading
            : pickupInitialLoading
        : hasSelectedDelivery
          ? deliveryFetching
          : pickupFetching,
      modesToRender,
      pickupLabel,
      pickupSchedule,
      refetchCurbside,
      refetchPickup,
      refetchDelivery,
    };
  }, [
    curbsideFetching,
    curbsideFlagOn,
    curbsideInitialLoading,
    curbsideSchedule,
    curbsideSlots?.reservation_slots?.length,
    deliveryFetching,
    deliveryInitialLoading,
    deliverySchedule,
    deliverySlots?.reservation_slots?.length,
    hasAddress,
    isAcceptingDeliveryOrders,
    modesToRender,
    pickupFetching,
    pickupInitialLoading,
    pickupSchedule,
    pickupSlots?.reservation_slots?.length,
    refetchCurbside,
    refetchDelivery,
    refetchPickup,
    reservationMode,
    store,
  ]);
};
